// =============================================================================
// ABOUT PAGE COMPONENT
// =============================================================================

import React from 'react';
import { Navbar, Footer } from '@/components/Layout';
import { ContentRenderer } from '@/components/ContentRenderer';
import { SEOHead } from '@/components/SEO';
import { LoadingPage, ErrorPage } from '@/components/LoadingPage';
import { usePageData } from '@/hooks/usePageData';
import { seoData } from '@/data/seoData';
import { navItems as defaultNavItems, footerLinks as defaultFooterLinks, socialLinks as defaultSocialLinks } from '@/data/homeContent';

export const AboutPage: React.FC = () => {
  // Load page data from Firebase Storage with fallback to local
  const { data, loading, error, reload } = usePageData('about');

  // Show loading state
  if (loading) {
    return <LoadingPage message="Loading about page..." />;
  }

  // Show error state with retry
  if (error) {
    return <ErrorPage error={error} onRetry={reload} />;
  }

  // No data available
  if (!data) {
    return <ErrorPage error={new Error('No data available')} onRetry={reload} />;
  }

  const seo = data.seo || seoData.about;
  const navItems = data.navigation?.navItems || defaultNavItems;
  const footerLinks = data.navigation?.footerLinks || defaultFooterLinks;
  const socialLinks = data.navigation?.socialLinks || defaultSocialLinks;
  
  // Handle SEO data safely
  const seoTitle = typeof seo.title === 'string' ? seo.title : seo.title;
  const seoKeywords = Array.isArray(seo.keywords) ? seo.keywords.join(', ') : seo.keywords;
  const seoCanonical = 'canonicalUrl' in seo ? (seo as any).canonicalUrl : undefined;
  const seoStructured = 'structuredData' in seo ? (seo as any).structuredData : undefined;
  
  return (
    <div className="about-page">
      <SEOHead
        title={seoTitle}
        description={seo.description}
        keywords={seoKeywords}
        canonicalUrl={seoCanonical}
        structuredData={seoStructured}
      />
      
      <Navbar 
        brand="Alex Finger Consulting"
        navItems={navItems}
        showThemeSwitcher={false}
      />
      
      <main className="main-content">
        <ContentRenderer components={data.components} />
      </main>
      
      <Footer
        brand="Alex Finger Consulting"
        description="Web development and business strategy services. Transform your ideas into digital reality."
        links={footerLinks}
        socialLinks={socialLinks}
      />
    </div>
  );
};
